import React, { useState, useEffect, useRef } from 'react';
import { api } from '../apiConfig';
import { FiX, FiUser } from 'react-icons/fi';
import styles from './ArtistSearch.module.css';

const ArtistSearch = ({ 
  selectedArtist, 
  onSelect, 
  placeholder = "Search for artist...",
  required = false 
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newArtistForm, setNewArtistForm] = useState({
    name: '',
    email: '',
    location: ''
  });
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current && 
        !searchRef.current.contains(event.target) &&
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search for artists when query changes
  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setLoading(true);
      setError('');
      
      try {
        const response = await api.get(`/users/search?role=artist&q=${encodeURIComponent(query)}`);
        setResults(response.data.users || []);
        setIsOpen(true);
      } catch (err) {
        setError('Failed to search artists');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300); // Debounce search

    return () => clearTimeout(searchTimeout);
  }, [query]);

  const handleSelect = (artist) => {
    onSelect(artist);
    setQuery(artist.name);
    setIsOpen(false);
    setShowCreateForm(false);
  };

  const handleClear = () => {
    onSelect(null);
    setQuery('');
    setIsOpen(false);
    setShowCreateForm(false);
    setNewArtistForm({ name: '', email: '', location: '' });
  };

  const handleCreateNew = () => {
    setNewArtistForm({ ...newArtistForm, name: query });
    setShowCreateForm(true);
    setIsOpen(false);
  };

  const handleCreateFormChange = (field, value) => {
    setNewArtistForm(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateSubmit = async () => {
    setCreateLoading(true);
    setError('');

    try {
      const response = await api.post('/users/create-artist', {
        name: newArtistForm.name.trim(),
        email: newArtistForm.email.trim() || null,
        location: newArtistForm.location.trim() || null
      });

      const newArtist = response.data.artist;
      onSelect(newArtist);
      setQuery(newArtist.name);
      setShowCreateForm(false);
      setNewArtistForm({ name: '', email: '', location: '' });
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.existing_artist) {
        // Artist with this email already exists, offer to use them instead
        const useExisting = window.confirm(
          `An artist with email "${errorData.existing_artist.email}" already exists: ${errorData.existing_artist.name}.\n\nWould you like to use this existing artist instead?`
        );
        if (useExisting) {
          onSelect(errorData.existing_artist);
          setQuery(errorData.existing_artist.name);
          setShowCreateForm(false);
          setNewArtistForm({ name: '', email: '', location: '' });
          setCreateLoading(false);
          return;
        }
      }
      setError(errorData?.error || 'Failed to create artist');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewArtistForm({ name: '', email: '', location: '' });
    setError('');
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    if (!e.target.value) {
      setResults([]);
      setIsOpen(false);
    }
  };

  const handleInputFocus = () => {
    if (query.length >= 2 && results.length > 0) {
      setIsOpen(true);
    }
  };

  return (
    <div className={styles.artistSearch}>
      <div className={styles.searchContainer} ref={searchRef}>
        <div className={styles.inputWrapper}>
          <input
            type="text"
            value={selectedArtist ? selectedArtist.name : query}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            className={styles.searchInput}
            required={required}
            disabled={selectedArtist}
          />
          {selectedArtist && (
            <button
              type="button"
              onClick={handleClear}
              className={styles.clearButton}
            >
              <FiX />
            </button>
          )}
        </div>

        {loading && (
          <div className={styles.loadingIndicator}>
            <div className={styles.spinner}></div>
            <span>Searching artists...</span>
          </div>
        )}

        {error && (
          <div className={styles.error}>
            {error}
          </div>
        )}

        {isOpen && results.length > 0 && (
          <div className={styles.dropdown} ref={dropdownRef}>
            {results.map((artist) => (
              <div
                key={artist.id}
                className={styles.resultItem}
                onClick={() => handleSelect(artist)}
              >
                <div className={styles.artistInfo}>
                  {artist.avatar ? (
                    <img 
                      src={artist.avatar} 
                      alt={artist.name}
                      className={styles.avatar}
                    />
                  ) : (
                    <div className={styles.avatarPlaceholder}>
                      <FiUser />
                    </div>
                  )}
                  <div className={styles.artistDetails}>
                    <div className={styles.artistName}>{artist.name}</div>
                    {artist.location && (
                      <div className={styles.artistLocation}>{artist.location}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {isOpen && query.length >= 2 && results.length === 0 && !loading && (
          <div className={styles.dropdown}>
            <div className={styles.noResults}>
              No artists found for "{query}"
            </div>
            <div className={styles.createOption} onClick={handleCreateNew}>
              <div className={styles.createIcon}>+</div>
              <div className={styles.createText}>
                <div className={styles.createTitle}>Create new artist "{query}"</div>
                <div className={styles.createSubtitle}>Add this artist to Art Emerge</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {selectedArtist && (
        <div className={styles.selectedArtist}>
          <div className={styles.selectedInfo}>
            {selectedArtist.avatar ? (
              <img 
                src={selectedArtist.avatar} 
                alt={selectedArtist.name}
                className={styles.selectedAvatar}
              />
            ) : (
              <div className={styles.selectedAvatarPlaceholder}>
                <FiUser />
              </div>
            )}
            <div>
              <div className={styles.selectedName}>{selectedArtist.name}</div>
              {selectedArtist.location && (
                <div className={styles.selectedLocation}>{selectedArtist.location}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {showCreateForm && (
        <div className={styles.createForm}>
          <h4 className={styles.createFormTitle}>Create New Artist</h4>
          <div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Artist Name *</label>
              <input
                type="text"
                value={newArtistForm.name}
                onChange={(e) => handleCreateFormChange('name', e.target.value)}
                className={styles.formInput}
                required
                placeholder="Enter artist's full name"
              />
            </div>
            
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Email (optional)</label>
              <input
                type="email"
                value={newArtistForm.email}
                onChange={(e) => handleCreateFormChange('email', e.target.value)}
                className={styles.formInput}
                placeholder="artist@example.com"
              />
              <small className={styles.formHelp}>
                If provided, the artist will be notified about their artwork being added.
              </small>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Location (optional)</label>
              <input
                type="text"
                value={newArtistForm.location}
                onChange={(e) => handleCreateFormChange('location', e.target.value)}
                className={styles.formInput}
                placeholder="City, State/Country"
              />
            </div>

            <div className={styles.formActions}>
              <button
                type="button"
                onClick={handleCancelCreate}
                className={styles.cancelButton}
                disabled={createLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateSubmit}
                className={styles.createButton}
                disabled={createLoading || !newArtistForm.name.trim()}
              >
                {createLoading ? 'Creating...' : 'Create Artist'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistSearch; 